import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock, ArrowRight, Timer } from "lucide-react";
import api from "@/lib/api";
import { HERO_IMG, TSHIRT, BLOOD, GENDERS, DEFAULT_TERMS, inr, longDate, ageOn, ageLabel, startPayment, errMsg } from "./runShared";
import { Field, Section, RunCard, AadhaarPicker, PolicyModal, inputCls, RUN_STYLES } from "./RunFormParts";

const EMPTY = {
  full_name: "", gender: "", date_of_birth: "", mobile: "", email: "", city: "Chhatrapati Sambhaji Nagar", address: "",
  tshirt_size: "", blood_group: "", emergency_contact_name: "", emergency_contact_phone: "",
  medical_conditions: "", running_club: "", previous_best_time: "",
};
const MOBILE_RE = /^[6-9]\d{9}$/;

const PolicyLink = ({ onClick, testid, children }) => (
  <button type="button" onClick={(e) => { e.preventDefault(); onClick(); }} className="text-[#0D5C63] font-semibold underline underline-offset-2" data-testid={testid}>{children}</button>
);

function validate(f, run, aadhaar, accept) {
  if (!run) return "Please choose a run";
  if (f.full_name.trim().length < 3) return "Please enter your full name";
  const tooLong = [["full_name", "Full name"], ["email", "Email"], ["city", "City"], ["emergency_contact_name", "Emergency contact name"], ["running_club", "Running club"]].find(([k]) => f[k].trim().length > 30);
  if (tooLong) return `${tooLong[1]} must not exceed 30 characters`;
  if (!f.gender) return "Please select gender";
  if (!f.date_of_birth) return "Please enter date of birth";
  const age = ageOn(f.date_of_birth, run.event_date);
  if (age == null || age < 1) return "Please enter a valid date of birth";
  if (run.min_age && age < run.min_age) return `${run.name} needs minimum age ${run.min_age} on event day (you will be ${age})`;
  if (run.max_age && age > run.max_age) return `${run.name} allows maximum age ${run.max_age} on event day (you will be ${age})`;
  if (!MOBILE_RE.test(f.mobile)) return "Please enter a valid 10-digit mobile number";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return "Please enter a valid email";
  if (!f.city.trim() || !f.address.trim()) return "Please enter city and address";
  if (!f.tshirt_size) return "Please select T-shirt size";
  if (!f.blood_group) return "Please select blood group";
  if (!f.emergency_contact_name.trim() || !MOBILE_RE.test(f.emergency_contact_phone)) return "Please enter emergency contact name and a valid 10-digit phone";
  if (f.emergency_contact_phone === f.mobile) return "Emergency phone must differ from your mobile";
  if (!aadhaar) return "Please upload your Aadhaar card";
  if (!accept) return "Please agree to the Terms and Conditions, Cancellation and Refund Policy and Privacy Policy";
  return "";
}

function Hero({ runs, event }) {
  const words = (event.name || "Run Registration").trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : "";
  return (
    <header className="relative overflow-hidden bg-[#08363A] text-white run-stripe">
      {event.loaded && <img src={event.banner_url || HERO_IMG} alt="Marathon banner" className="absolute inset-0 w-full h-full object-cover opacity-30" data-testid="run-hero-image" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#08363A] via-[#08363A]/85 to-transparent" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#F26419] bg-white/10 px-3 py-1.5 rounded-full">
          <Timer size={13} /> Gramaudyogik Shikshan Mandal
        </div>
        <h1 className="run-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mt-5 max-w-3xl" data-testid="run-hero-title">
          {words.join(" ")}{last && <> <span className="text-[#F26419]">{last}</span></>}
        </h1>
        {event.description && <p className="mt-5 text-sm sm:text-base text-white/80 max-w-xl whitespace-pre-line" data-testid="run-hero-description">{event.description}</p>}
        <div className="mt-8 flex flex-wrap gap-3">
          {runs.map((r) => (
            <span key={r.id} className="run-display text-2xl px-4 py-1.5 rounded-full border border-white/25 bg-white/5">{r.distance_km} KM · {inr(r.fee)}</span>
          ))}
        </div>
      </div>
    </header>
  );
}

function Summary({ run, form, submitting, onSubmit }) {
  const age = run ? ageOn(form.date_of_birth, run.event_date) : null;
  return (
    <aside className="lg:sticky lg:top-6 bg-[#08363A] text-white rounded-2xl p-6 run-stripe" data-testid="run-summary">
      <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-semibold">Your Entry</div>
      {run ? (
        <>
          <div className="run-display text-5xl mt-2">{run.distance_km} KM</div>
          <div className="text-sm text-white/80">{run.name}</div>
          <div className="mt-4 space-y-2 text-xs text-white/75 border-t border-white/15 pt-4">
            <div className="flex justify-between"><span>Event date</span><b className="text-white">{longDate(run.event_date)}</b></div>
            <div className="flex justify-between"><span>Age limit</span><b className="text-white">{ageLabel(run)}</b></div>
            {age != null && age > 0 && <div className="flex justify-between"><span>Your age on event day</span><b className="text-white" data-testid="run-summary-age">{age} yrs</b></div>}
          </div>
          <div className="mt-4 border-t border-white/15 pt-4 flex items-end justify-between">
            <span className="text-xs text-white/70">Registration fee</span>
            <span className="run-display text-4xl text-[#F26419]" data-testid="run-summary-fee">{inr(run.fee)}</span>
          </div>
        </>
      ) : (
        <p className="mt-3 text-sm text-white/70">Choose a run to see your entry summary.</p>
      )}
      <button
        type="button" onClick={onSubmit} disabled={submitting || !run}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#F26419] hover:bg-white hover:text-[#08363A] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-sm py-3.5 rounded-full transition-colors"
        data-testid="run-submit-btn"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
        {submitting ? "Please wait…" : run ? `Pay ${inr(run.fee)} & Register` : "Select a run"}
        {!submitting && <ArrowRight size={15} />}
      </button>
      <div className="mt-3 flex items-center gap-2 text-[11px] text-white/60"><ShieldCheck size={13} /> Secure payment via CCAvenue (HDFC). Fee is set by GS Mandal and cannot be changed.</div>
    </aside>
  );
}

export default function RunRegister() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [runId, setRunId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [aadhaar, setAadhaar] = useState(null);
  const [accept, setAccept] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState({ name: "", description: "", banner_url: "", loaded: false });

  useEffect(() => {
    api.get("/public/marathon/event").then(({ data }) => {
      setEvent({ ...data, loaded: true });
      if (data.name) document.title = `${data.name} · Registration`;
    }).catch(() => setEvent((e) => ({ ...e, loaded: true })));
  }, []);
  useEffect(() => {
    api.get("/public/marathon/runs").then(({ data }) => {
      setRuns(data);
      const open = data.filter((r) => r.is_open);
      const wanted = open.find((r) => r.id === params.get("run"));
      if (wanted) setRunId(wanted.id);
      else if (open.length === 1) setRunId(open[0].id);
    }).catch(() => setRuns([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useMemo(() => (runs || []).find((r) => r.id === runId) || null, [runs, runId]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setDigits = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value.replace(/\D/g, "").slice(0, 10) }));

  const submit = async () => {
    const err = validate(form, run, aadhaar, accept);
    if (err) return toast.error(err);
    setSubmitting(true);
    let token = "";
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v.trim()));
      fd.append("run_id", run.id);
      fd.append("accept_terms", "true");
      fd.append("aadhaar", aadhaar, aadhaar.name);
      const { data } = await api.post("/public/marathon/register", fd);
      token = data.token;
      toast.success(`Registration ${data.registration_no} saved. Redirecting to payment…`);
      await startPayment(token);
    } catch (e) {
      toast.error(errMsg(e, token ? "Could not start payment" : "Registration failed"));
      if (token) navigate(`/run/register/status/${token}`);
      setSubmitting(false);
    }
  };

  if (runs === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF6EB] text-sm text-[#5B5B5B]" data-testid="run-loading"><Loader2 className="animate-spin mr-2" size={16} />Loading…</div>;
  }

  return (
    <div className="run-page min-h-screen bg-[#FAF6EB] text-[#1A1A1A]" data-testid="run-register-page">
      <style>{RUN_STYLES}</style>
      <Hero runs={runs} event={event} />
      {runs.length === 0 ? (
        <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="run-no-runs">
          <h2 className="run-display text-3xl uppercase text-[#08363A]">Registrations are not open yet</h2>
          <p className="text-sm text-[#5B5B5B] mt-2">Please check back soon or write to info@gsmandal.org.</p>
        </div>
      ) : (
        <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
          <div className="space-y-6">
            <Section n={1} title="Choose your run" sub="Fee and age limits are set by GS Mandal." testid="run-section-choose">
              <div className="grid sm:grid-cols-2 gap-4">
                {runs.map((r) => <RunCard key={r.id} run={r} selected={r.id === runId} onSelect={(x) => setRunId(x.id)} />)}
              </div>
            </Section>

            <Section n={2} title="Participant details" testid="run-section-personal">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name (as on Aadhaar)" required full><input className={inputCls} value={form.full_name} onChange={set("full_name")} maxLength={30} data-testid="run-full-name" /></Field>
                <Field label="Gender" required>
                  <select className={inputCls} value={form.gender} onChange={set("gender")} data-testid="run-gender">
                    <option value="">Select</option>{GENDERS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Date of birth" required hint={run && form.date_of_birth ? `Age on event day: ${ageOn(form.date_of_birth, run.event_date)} yrs` : ""}>
                  <input type="date" className={inputCls} value={form.date_of_birth} onChange={set("date_of_birth")} max={new Date().toISOString().slice(0, 10)} data-testid="run-dob" />
                </Field>
                <Field label="Mobile" required><input inputMode="numeric" className={inputCls} value={form.mobile} onChange={setDigits("mobile")} placeholder="10-digit mobile" data-testid="run-mobile" /></Field>
                <Field label="Email" required><input type="email" className={inputCls} value={form.email} onChange={set("email")} maxLength={30} data-testid="run-email" /></Field>
                <Field label="City" required><input className={inputCls} value={form.city} onChange={set("city")} maxLength={30} data-testid="run-city" /></Field>
                <Field label="Address" required full><textarea rows={2} className={inputCls} value={form.address} onChange={set("address")} maxLength={400} data-testid="run-address" /></Field>
              </div>
            </Section>

            <Section n={3} title="Race kit & health" testid="run-section-health">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="T-shirt size" required>
                  <select className={inputCls} value={form.tshirt_size} onChange={set("tshirt_size")} data-testid="run-tshirt">
                    <option value="">Select</option>{TSHIRT.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Blood group" required>
                  <select className={inputCls} value={form.blood_group} onChange={set("blood_group")} data-testid="run-blood">
                    <option value="">Select</option>{BLOOD.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Emergency contact name" required><input className={inputCls} value={form.emergency_contact_name} onChange={set("emergency_contact_name")} maxLength={30} data-testid="run-ec-name" /></Field>
                <Field label="Emergency contact phone" required><input inputMode="numeric" className={inputCls} value={form.emergency_contact_phone} onChange={setDigits("emergency_contact_phone")} data-testid="run-ec-phone" /></Field>
                <Field label="Medical conditions / allergies" full hint="Leave blank if none"><textarea rows={2} className={inputCls} value={form.medical_conditions} onChange={set("medical_conditions")} maxLength={500} data-testid="run-medical" /></Field>
              </div>
            </Section>

            <Section n={4} title="Running profile" sub="Optional" testid="run-section-profile">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Running club"><input className={inputCls} value={form.running_club} onChange={set("running_club")} maxLength={30} data-testid="run-club" /></Field>
                <Field label="Previous best time" hint="e.g. 00:52:30 for 10 KM"><input className={inputCls} value={form.previous_best_time} onChange={set("previous_best_time")} maxLength={40} data-testid="run-best-time" /></Field>
              </div>
            </Section>

            <Section n={5} title="Identity & declaration" testid="run-section-declaration">
              <Field label="Aadhaar card copy" required><AadhaarPicker file={aadhaar} onChange={setAadhaar} /></Field>
              <label className="mt-5 flex items-start gap-3 text-sm cursor-pointer" data-testid="run-accept-wrap">
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#F26419]" data-testid="run-accept" />
                <span data-testid="run-accept-text">
                  I have read and agree to the{" "}
                  <PolicyLink testid="run-terms-link" onClick={() => setPolicy({ title: "Terms and Conditions", html: run?.terms_and_conditions?.trim() || DEFAULT_TERMS })}>Terms and Conditions</PolicyLink>,{" "}
                  <PolicyLink testid="run-refund-link" onClick={() => setPolicy({ title: "Cancellation and Refund Policy", html: event.cancellation_policy })}>Cancellation and Refund Policy</PolicyLink>, and{" "}
                  <PolicyLink testid="run-privacy-link" onClick={() => setPolicy({ title: "Privacy Policy", html: event.privacy_policy })}>Privacy Policy</PolicyLink>.
                  {" "}I confirm that the information I have provided is true and accurate.
                </span>
              </label>
            </Section>
          </div>
          <Summary run={run} form={form} submitting={submitting} onSubmit={submit} />
        </main>
      )}
      <footer className="border-t border-[#E9E2D2] py-6 text-center text-[11px] text-[#8a8478]">© Gramaudyogik Shikshan Mandal · info@gsmandal.org</footer>
      <PolicyModal policy={policy} onClose={() => setPolicy(null)} />
    </div>
  );
}
